// Configuració i pujada de contractes a un NAS via WebDAV
// Es guarda la configuració a localStorage. Quan estigui configurat i "enabled",
// s'intenta pujar el PDF via HTTP PUT amb Basic Auth.
//
// Requisits per què funcioni:
//   - El NAS ha de tenir WebDAV habilitat i URL HTTPS pública
//   - CORS: el NAS ha de permetre l'origen del web (Access-Control-Allow-Origin)
//   - Usuari/contrasenya amb permís d'escriptura a la carpeta destí
//
// Compatibilitats testejades: Synology (WebDAV Server), Nextcloud (WebDAV nadiu),
// QNAP (Web Server), TrueNAS Scale.

const CONFIG_KEY = 'gdc_nas_config_v1';

const defaults = {
  enabled: false,
  baseUrl: '',   // https://nas.example.com/dav/gatsdelcarrer
  username: '',
  password: '',
  folder: 'contractes',  // subcarpeta dins baseUrl (opcional)
};

export const getNasConfig = () => {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    return raw ? { ...defaults, ...JSON.parse(raw) } : { ...defaults };
  } catch (e) {
    return { ...defaults };
  }
};

export const saveNasConfig = (patch) => {
  const merged = { ...getNasConfig(), ...patch };
  localStorage.setItem(CONFIG_KEY, JSON.stringify(merged));
  return merged;
};

const buildUrl = (config, filename) => {
  const base = config.baseUrl.replace(/\/+$/, '');
  const folder = (config.folder || '').replace(/^\/+|\/+$/g, '');
  const encoded = encodeURIComponent(filename);
  return folder ? `${base}/${folder}/${encoded}` : `${base}/${encoded}`;
};

// Comprova si el fitxer ja existeix (HEAD). Retorna true/false o null si error.
export const contractExists = async (filename) => {
  const config = getNasConfig();
  if (!config.enabled || !config.baseUrl) return null;
  try {
    const url = buildUrl(config, filename);
    const res = await fetch(url, {
      method: 'HEAD',
      headers: { Authorization: 'Basic ' + btoa(`${config.username}:${config.password}`) },
    });
    return res.ok;
  } catch (e) {
    return null;
  }
};

// Puja un Blob PDF al NAS via WebDAV PUT.
// Retorna { ok, status, url, error }.
export const uploadContractPdf = async (pdfBlob, filename) => {
  const config = getNasConfig();
  if (!config.enabled) return { ok: false, error: 'DISABLED' };
  if (!config.baseUrl) return { ok: false, error: 'NO_URL' };

  const url = buildUrl(config, filename);
  try {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: 'Basic ' + btoa(`${config.username}:${config.password}`),
        'Content-Type': 'application/pdf',
      },
      body: pdfBlob,
    });
    return { ok: res.ok, status: res.status, url };
  } catch (e) {
    return { ok: false, error: e.message || 'NETWORK' };
  }
};

// Test ràpid de connexió (PROPFIND o HEAD sobre la carpeta base).
export const testNasConnection = async () => {
  const config = getNasConfig();
  if (!config.baseUrl) return { ok: false, error: 'NO_URL' };
  try {
    const url = config.baseUrl.replace(/\/+$/, '') + '/';
    const res = await fetch(url, {
      method: 'PROPFIND',
      headers: {
        Authorization: 'Basic ' + btoa(`${config.username}:${config.password}`),
        Depth: '0',
      },
    });
    return { ok: res.ok, status: res.status };
  } catch (e) {
    return { ok: false, error: e.message || 'NETWORK' };
  }
};
